/**
 * Route constants for the Hailey Intake System
 * Centralized route management with parameter support
 */

export const ROUTES = {
  // Homepage
  HOME: '/',

  // Development
  DEV: '/dev',

  // Client onboarding flow
  GET_STARTED: {
    BASE: '/get-started',
    INTAKE: '/get-started/intake',
    VERIFY: '/get-started/verify',
    ASSESSMENT: '/get-started/assessment',
    COMPLETE: '/get-started/complete',
  },

  // Client portal (authenticated clients)
  PORTAL: {
    BASE: '/portal',
    LOGIN: '/portal/login',
    DASHBOARD: '/portal/dashboard',
    SESSIONS: '/portal/sessions',
    RESOURCES: '/portal/resources',
    BILLING: '/portal/billing',
  },

  // Healer admin area
  HEALER: {
    BASE: '/healer',
    LOGIN: '/healer/login',
    DASHBOARD: '/healer/dashboard',
    CLIENTS: '/healer/clients',
    CLIENT_DETAIL: '/healer/clients/:id',
    SCHEDULE: '/healer/schedule',
    ANALYTICS: '/healer/analytics',
    CONTENT: '/healer/content',
    SETTINGS: '/healer/settings',
  },

  // Public booking
  SCHEDULE: {
    BOOK: '/schedule/book',
    CONFIRMED: '/schedule/confirmed',
  },

  // Public resources
  RESOURCES: {
    BASE: '/resources',
    CATALOG: '/resources/catalog',
    COMMUNITY: '/resources/community',
    TOOLS: '/resources/tools',
  },

  // Checkout flow
  CHECKOUT: {
    BASE: '/checkout',
    SUCCESS: '/checkout/success',
  },

  // Authentication
  AUTH: {
    CALLBACK: '/auth/callback',
  },

  // Legal pages
  LEGAL: {
    BASE: '/legal',
    PRIVACY: '/legal/privacy',
    TERMS: '/legal/terms',
    HIPAA: '/legal/hipaa',
  },
} as const;

// Route parameter types for type safety
export interface RouteParams {
  // Get started flow
  intake?: {
    ref?: string;
    promo?: string;
    healer?: string;
  };
  verify?: {
    email?: string;
    token?: string;
    resent?: boolean;
  };
  assessment?: {
    step?: number;
    section?: 'personal' | 'health' | 'goals' | 'preferences' | 'consent';
    saved?: boolean;
    return?: boolean;
  };
  complete?: {
    intakeId?: string;
    nextStep?: 'book' | 'wait' | 'portal';
    healerAssigned?: string;
  };

  // Portal
  portalLogin?: {
    redirect?: string;
    expired?: boolean;
    verified?: boolean;
  };
  portalDashboard?: {
    welcome?: boolean;
    notification?: string;
  };
  portalSessions?: {
    view?: 'upcoming' | 'past' | 'all';
    filter?: 'individual' | 'group' | 'workshop';
    healer?: string;
    date?: string;
    status?: 'confirmed' | 'pending' | 'cancelled';
  };
  portalResources?: {
    category?: 'meditation' | 'journaling' | 'exercises' | 'videos';
    access?: 'owned' | 'available' | 'recommended';
    search?: string;
    sort?: 'newest' | 'popular' | 'alphabetical';
    page?: number;
  };
  portalBilling?: {
    view?: 'history' | 'methods' | 'subscriptions';
    invoice?: string;
    year?: number;
    status?: 'paid' | 'pending' | 'failed';
  };

  // Healer admin
  healerLogin?: {
    redirect?: string;
    setup?: boolean;
  };
  healerDashboard?: {
    period?: 'today' | 'week' | 'month' | 'custom';
    metric?: 'revenue' | 'sessions' | 'clients';
    alert?: string;
  };
  healerClients?: {
    status?: 'active' | 'inactive' | 'prospective' | 'archived';
    filter?: 'new' | 'returning' | 'at-risk';
    search?: string;
    sort?: 'name' | 'last-session' | 'created';
    tag?: string;
    page?: number;
  };
  healerClientDetail?: {
    tab?: 'overview' | 'notes' | 'sessions' | 'documents' | 'billing';
    sessionId?: string;
    action?: 'edit' | 'schedule' | 'message';
    return?: string;
  };
  healerSchedule?: {
    view?: 'calendar' | 'list' | 'availability';
    date?: string;
    week?: string;
    month?: string;
    type?: 'all' | 'individual' | 'group';
    location?: 'office' | 'virtual' | 'both';
  };
  healerAnalytics?: {
    report?: 'revenue' | 'utilization' | 'retention' | 'growth';
    period?: 'week' | 'month' | 'quarter' | 'year' | 'custom';
    compare?: boolean;
    breakdown?: 'service' | 'client' | 'day';
    export?: 'csv' | 'pdf';
  };
  healerContent?: {
    tab?: 'library' | 'assignments' | 'recommendations';
    type?: 'all' | 'documents' | 'videos' | 'exercises';
    status?: 'published' | 'draft' | 'archived';
    search?: string;
  };
  healerSettings?: {
    section?: 'profile' | 'practice' | 'availability' | 'billing' | 'integrations';
    saved?: boolean;
    wizard?: boolean;
  };

  // Public resources
  resourcesCatalog?: {
    category?: 'mental-health' | 'wellness' | 'self-help';
    type?: 'course' | 'ebook' | 'meditation' | 'workshop';
    price?: 'free' | 'paid' | 'subscription';
    level?: 'beginner' | 'intermediate' | 'advanced';
    healer?: string;
    search?: string;
    sort?: 'featured' | 'newest' | 'popular' | 'price';
    page?: number;
  };
  resourcesCommunity?: {
    tab?: 'discussions' | 'events' | 'groups';
    topic?: string;
    filter?: 'trending' | 'recent' | 'following';
    type?: 'online' | 'in-person' | 'hybrid';
  };
  resourcesTools?: {
    platform?: 'ios' | 'android' | 'web' | 'all';
    category?: 'meditation' | 'tracking' | 'journaling';
    price?: 'free' | 'paid' | 'freemium';
    sort?: 'rating' | 'downloads' | 'newest';
  };

  // Checkout
  checkoutSuccess?: {
    orderId?: string;
    type?: 'session' | 'content' | 'subscription';
    itemId?: string;
    receipt?: 'email' | 'download';
  };

  // Auth callback
  authCallback?: {
    code?: string;
    state?: string;
    error?: string;
    provider?: 'google' | 'facebook' | 'email';
  };
}

// Helper functions for building routes with parameters
export const buildRoute = {
  getStartedIntake: (params?: RouteParams['intake']) => {
    const url = new URL(ROUTES.GET_STARTED.INTAKE, window.location.origin);
    if (params?.ref) url.searchParams.set('ref', params.ref);
    if (params?.promo) url.searchParams.set('promo', params.promo);
    if (params?.healer) url.searchParams.set('healer', params.healer);
    return url.pathname + url.search;
  },

  healerClients: (params?: RouteParams['healerClients']) => {
    const url = new URL(ROUTES.HEALER.CLIENTS, window.location.origin);
    if (params?.status) url.searchParams.set('status', params.status);
    if (params?.filter) url.searchParams.set('filter', params.filter);
    if (params?.search) url.searchParams.set('search', params.search);
    if (params?.sort) url.searchParams.set('sort', params.sort);
    if (params?.tag) url.searchParams.set('tag', params.tag);
    if (params?.page) url.searchParams.set('page', params.page.toString());
    return url.pathname + url.search;
  },

  healerClientDetail: (id: string, params?: RouteParams['healerClientDetail']) => {
    const url = new URL(ROUTES.HEALER.CLIENT_DETAIL.replace(':id', id), window.location.origin);
    if (params?.tab) url.searchParams.set('tab', params.tab);
    if (params?.sessionId) url.searchParams.set('sessionId', params.sessionId);
    if (params?.action) url.searchParams.set('action', params.action);
    if (params?.return) url.searchParams.set('return', params.return);
    return url.pathname + url.search;
  },

  // Add more helper functions as needed
};

// Legacy route mappings for migration
export const LEGACY_ROUTE_MAPPING = {
  '/client': ROUTES.PORTAL.LOGIN,
  '/client/portal': ROUTES.PORTAL.DASHBOARD,
  '/intake': ROUTES.GET_STARTED.INTAKE,
  '/intake-form': ROUTES.GET_STARTED.ASSESSMENT,
  '/auth/verify': ROUTES.GET_STARTED.VERIFY,
  '/auth/verify-intake': ROUTES.GET_STARTED.VERIFY,
  '/submission-success': ROUTES.GET_STARTED.COMPLETE,
  '/admin': ROUTES.HEALER.DASHBOARD,
  '/admin/client/:id': ROUTES.HEALER.CLIENT_DETAIL,
  '/book': ROUTES.SCHEDULE.BOOK,
  '/booking-success': ROUTES.SCHEDULE.CONFIRMED,
  '/catalog': ROUTES.RESOURCES.CATALOG,
  '/catalog/purchase-success': ROUTES.CHECKOUT.SUCCESS,
  '/personalized': ROUTES.PORTAL.RESOURCES,
  '/community': ROUTES.RESOURCES.COMMUNITY,
  '/software': ROUTES.RESOURCES.TOOLS,
} as const;